/**
 * Allys Locations Grid Component
 * 
 * Premium styled locations display with Allys design language
 */

'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import type { LocationsGridProps, Location } from '@routeroyal/theme-core';

/**
 * Allys Locations Grid - Elegant location showcase
 */
export function AllysLocationsGrid({
  title,
  subtitle,
  locations,
  style = 'cards'
}: LocationsGridProps) {
  return (
    <section className="py-24 lg:py-32 bg-gray-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6 tracking-tight [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]"
          >
            {title}
          </motion.h2>
          
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto font-light [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]"
            >
              {subtitle}
            </motion.p>
          )}
        </div>
        
        {/* Cards Style */}
        {style === 'cards' && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-7">
            {locations.map((location, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.6, delay: index * 0.05, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                {location.link ? (
                  <a
                    href={location.link}
                    className="block group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500"
                  >
                    <LocationCard location={location} />
                  </a>
                ) : (
                  <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                    <LocationCard location={location} />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
        
        {/* Simple Style */}
        {style === 'simple' && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 lg:gap-10">
            {locations.map((location, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.6, delay: index * 0.05, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="text-center"
              >
                {location.link ? (
                  <a 
                    href={location.link}
                    className="group block"
                  >
                    <h3 className="text-lg lg:text-xl font-heading font-semibold text-gray-900 group-hover:text-gray-600 transition-colors duration-200 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                      {location.name}
                    </h3>
                    {location.description && (
                      <p className="text-sm lg:text-base text-gray-600 mt-2 font-light [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                        {location.description}
                      </p>
                    )}
                  </a>
                ) : (
                  <>
                    <h3 className="text-lg lg:text-xl font-heading font-semibold text-gray-900 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                      {location.name}
                    </h3>
                    {location.description && (
                      <p className="text-sm lg:text-base text-gray-600 mt-2 font-light [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                        {location.description}
                      </p>
                    )}
                  </>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// Helper component for location card
function LocationCard({ location }: { location: Location }) {
  return (
    <>
      {location.image && (
        <div className="relative aspect-[4/3] bg-gray-100">
          <Image
            src={location.image}
            alt={location.name}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
          />
        </div>
      )}
      <div className="p-5">
        <h3 className="text-base lg:text-lg font-heading font-semibold text-gray-900 group-hover:text-gray-600 transition-colors duration-200 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
          {location.name}
        </h3>
        {location.description && (
          <p className="text-sm text-gray-600 mt-2 font-light [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
            {location.description}
          </p>
        )}
      </div>
    </>
  );
}

